import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogRef} from '@angular/material/dialog';
import {EntityProcessingService} from '../../services/entity-processing.service';
import {SingleEntityLineProcessingService} from '../../services/single-entity-line-processing.service';
import {EntityProcessing} from '../../entity/entity-processing';
import {SingleEntityLineProcessing} from '../../entity/single-entity-line-processing';
import {ProcessingStatus} from '../../entity/processing-status';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';


@Component({
    selector: 'app-processing-status-dialog',
    templateUrl: './processing-status-dialog.component.html',
    styleUrls: ['./processing-status-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatProgressSpinner, MatDialogActions, MatButton]
})
export class ProcessingStatusDialogComponent implements OnInit {
  private readonly entityProcessingService = inject(EntityProcessingService);
  private readonly singleEntityLineProcessingService = inject(SingleEntityLineProcessingService);
  readonly dialogRef = inject<MatDialogRef<ProcessingStatusDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  statusRecords: MatTableDataSource<ProcessingStatus> = new MatTableDataSource<ProcessingStatus>();
  displayedColumns: string[] = ['entityId', 'entityRevision', 'productFamily', 'erpStatus', 'requestedShipDate', 'requestedEngineeringDate',
    'promisedEngineeringDate', 'scheduledShipDate', 'erpOrderNumber'];

  ngOnInit(): void {
    if (!this.data || !this.data.entityKey || this.data.entityKey.length === 0) {
      this.close();
    }
    this.isBusy.set(true);
    this.entityProcessingService.getEntityProcessingRecords(this.data.entityKey).subscribe(entityRecords => {
      this.singleEntityLineProcessingService.getEntitySingleEntityLineProcessingRecords(this.data.entityKey)
        .subscribe(lineRecords => {
          this.statusRecords = new MatTableDataSource<ProcessingStatus>(this.buildStatusRecordList(entityRecords, lineRecords));
          this.statusRecords.sort = this.sort;
          this.isBusy.set(false);
        }, error => this.handleError(error));
    }, error => this.handleError(error));
  }

  private handleError(error: any): void {
    this.errorMessage.set(error.error);
    this.isBusy.set(false);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  private buildStatusRecordList(entityRecords: EntityProcessing[], lineRecords: SingleEntityLineProcessing[]): ProcessingStatus[] {
    const statusRecords: ProcessingStatus[] = [];
    for (const entityRecord of entityRecords) {
      const statusRecord: ProcessingStatus = this.initializeStatusRecord(entityRecord);
      statusRecord.erpOrderNumber = this.getErpOrderNumber(statusRecord.productFamily, lineRecords);
      statusRecords.push(statusRecord);
    }
    return statusRecords;
  }

  private initializeStatusRecord(entityRecord: EntityProcessing): ProcessingStatus {
    const statusRecord = new ProcessingStatus();
    statusRecord.entityId = entityRecord.entityId;
    statusRecord.entityRevision = entityRecord.revision;
    statusRecord.erpStatus = entityRecord.erpStatus;
    statusRecord.productFamily = entityRecord.productFamily;
    statusRecord.promisedEngineeringDate = this.parseDate(entityRecord.engineeringPromisedDate);
    statusRecord.requestedEngineeringDate = this.parseDate(entityRecord.engineeringRequestedDate);
    statusRecord.requestedShipDate = this.parseDate(entityRecord.requestedDate);
    statusRecord.scheduledShipDate = this.parseDate(entityRecord.scheduleDate);
    return statusRecord;
  }

  private parseDate(input: string): string {
    if (!input || input.startsWith('1753')) {
      return '';
    }
    return input;
  }

  private getErpOrderNumber(productFamily: string, lineRecords: SingleEntityLineProcessing[]): string {
    if (lineRecords && lineRecords.length > 0) {
      const matchingOrders = [...new Set(lineRecords.filter(record => record.erpOrderNumber && record.erpOrderNumber > 0 &&
        record.product && record.product.family && record.product.family.code === productFamily)
        .map(record => record.erpOrderNumber.toString()))];
      if (matchingOrders && matchingOrders.length > 0) {
        return matchingOrders.join(', ');
      }
    }
    return '';
  }

}
